import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, Award, UserCheck, Clock, FileCheck, Server } from "lucide-react";

const credentials = [
  {
    icon: Shield,
    label: "SDVOSB",
    sublabel: "Certified",
  },
  {
    icon: Award,
    label: "Military & Veteran",
    sublabel: "Leadership",
  },
  {
    icon: UserCheck,
    label: "Cybersecurity",
    sublabel: "Certified Professionals",
  },
  {
    icon: Clock,
    label: "25+ Years",
    sublabel: "Combined Experience",
  },
  {
    icon: FileCheck,
    label: "CMMC",
    sublabel: "Expertise",
  },
  {
    icon: Server,
    label: "Program",
    sublabel: "Management",
  },
];

const TrustBar = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="trust"
      ref={ref}
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="trust-bar-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-slate-900/50" />
      
      {/* Dividers */}
      <div className="absolute top-0 left-0 right-0 section-divider" />
      <div className="absolute bottom-0 left-0 right-0 section-divider" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-slate-500 text-sm font-medium tracking-wider uppercase">
            Trusted Credentials
          </span>
        </motion.div>

        {/* Credentials Marquee */}
        <div className="trust-marquee overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex animate-marquee"
          >
            {/* Double the items for seamless loop */}
            {[...credentials, ...credentials].map((cred, index) => {
              const Icon = cred.icon;
              return (
                <div
                  key={`${cred.label}-${index}`}
                  className="flex-shrink-0 mx-6 md:mx-10"
                >
                  <div className="credential-badge flex items-center gap-4 px-6 py-4 rounded-xl">
                    <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{cred.label}</p>
                      <p className="text-slate-500 text-sm">{cred.sublabel}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        {/* Static Grid for Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:hidden gap-4 mt-8"
        >
          {credentials.slice(0, 4).map((cred) => {
            const Icon = cred.icon;
            return (
              <div
                key={cred.label}
                className="credential-badge flex flex-col items-center gap-2 p-4 rounded-xl text-center"
              >
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{cred.label}</p>
                  <p className="text-slate-500 text-xs">{cred.sublabel}</p>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default TrustBar;
